import React, { useEffect, useState } from "react";
import type { GlossaryEntry } from "../../../glossary";
import "./glossary-edit-modal.css";
import CcButton from "./CcButton";

export interface GlossaryEditModalProps {
  open: boolean;
  initialEntry: GlossaryEntry;
  saving?: boolean;
  onClose: () => void;
  onSave: (entry: GlossaryEntry) => void;
}

const GlossaryEditModal = ({
  open,
  initialEntry,
  saving = false,
  onClose,
  onSave,
}: GlossaryEditModalProps) => {
  const [title, setTitle] = useState(initialEntry.title);
  const [definition, setDefinition] = useState(initialEntry.definition);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setTitle(initialEntry.title);
    setDefinition(initialEntry.definition);
    setFieldErrors({});
  }, [open, initialEntry]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, saving]);

  const handleSubmit = () => {
    const nextTitle = title.trim();
    const nextDefinition = definition.trim();
    const errors: Record<string, string> = {};
    if (!nextTitle) errors.title = "Title is required.";
    if (!nextDefinition) errors.definition = "Definition is required.";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    onSave({ title: nextTitle, definition: nextDefinition });
  };

  if (!open) return null;

  return (
    <div
      className="dialog-scrim glossary-edit-modal-scrim"
      role="presentation"
      onClick={(event) => {
        if (saving) return;
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="dialog-card glossary-edit-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="glossary-edit-modal-title"
      >
        <div className="dialog-header glossary-edit-modal__header">
          <h2 className="dialog-title" id="glossary-edit-modal-title">
            Edit Glossary Term
          </h2>
          <CcButton variant="icon" aria-label="Close modal" onClick={onClose} disabled={saving}>
            ✕
          </CcButton>
        </div>

        <div className="dialog-body glossary-edit-modal-body">
          <form
            id="glossary-edit-modal-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
          >
            <div className="glossary-edit-form">
              <label className="field-label" htmlFor="glossary-edit-title">
                Title
              </label>
              <input
                id="glossary-edit-title"
                className="field-input"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                autoComplete="off"
                disabled={saving}
              />
              {fieldErrors.title ? (
                <p className="field-error">{fieldErrors.title}</p>
              ) : null}

              <label className="field-label" htmlFor="glossary-edit-definition">
                Definition
              </label>
              <textarea
                id="glossary-edit-definition"
                className="field-input glossary-edit-form__definition"
                value={definition}
                onChange={(event) => setDefinition(event.target.value)}
                rows={6}
                disabled={saving}
              />
              {fieldErrors.definition ? (
                <p className="field-error">{fieldErrors.definition}</p>
              ) : null}
            </div>
          </form>
        </div>

        <div className="dialog-footer glossary-edit-modal__footer">
          <CcButton variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </CcButton>
          <CcButton
            variant="primary"
            type="submit"
            form="glossary-edit-modal-form"
            loading={saving}
          >
            {saving ? "Saving…" : "Save"}
          </CcButton>
        </div>
      </div>
    </div>
  );
};

export default GlossaryEditModal;
